import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Skeleton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ProviderAvailabilityScheduleItem } from "./ProviderAvailability";
import { isTablet, isDesktop } from "react-device-detect";
import { BreadcrumbTexts, providerAvailabilityRequestTypes } from "constant/BiPortalConstants";

interface RequestedShift {
  days: number[];
  blockId?: number;
  shiftId?: number;
  endTime: string;
  startTime: string;
  daysOfWeek: string;
}

interface ShiftInformation {
  requestedShift: RequestedShift[];
  additionalNotes: string;
  newRequestStartDate: string;
  isSchoolScheduleChange: boolean;
}

interface SubmittedRequestAPI {
  providerId: number;
  isAdditionalShift: boolean;
  isRemovalRequest: boolean;
  shiftInformation: ShiftInformation;
  jiraTicketUrl: string | null;
}

interface SubmittedRequest {
  id: string;
  shiftType: string;
  requestedShift: string[];
  requestType: string;
  dateRequested: string;
  ticketId: string | null;
  jiraTicketUrl: string | null;
}

interface SubmittedRequestsProps {
  providerCurrentShifts: ProviderAvailabilityScheduleItem[];
  isLoading: boolean;
  submittedRequestsData: SubmittedRequestAPI[];
  isLoadingRequests: boolean;
}
const SubmittedRequests: React.FC<SubmittedRequestsProps> = ({ providerCurrentShifts, isLoading, submittedRequestsData, isLoadingRequests }) => {
  const [submittedRequests, setSubmittedRequests] = useState<
    SubmittedRequest[]
  >([]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  const transformAPIData = useCallback((apiData: SubmittedRequestAPI[]): SubmittedRequest[] => {
    return apiData.map((item, index) => {
      const shiftType = item.isAdditionalShift
        ? BreadcrumbTexts.additionalAvailability
        : BreadcrumbTexts.setShift;

      const requestType = item.isRemovalRequest
        ? providerAvailabilityRequestTypes.removeAvailability
        : item.isAdditionalShift
        ? providerAvailabilityRequestTypes.additionalAvailability
        : providerAvailabilityRequestTypes.setShift;

      const requestedShift = item.shiftInformation.requestedShift.map(
        (shift) =>
          `${shift.daysOfWeek} ${shift.startTime} - ${shift.endTime}`
      );

      const dateRequested = formatDate(
        item.shiftInformation.newRequestStartDate
      );

      let ticketId: string | null = null;
      if (item.jiraTicketUrl) {
        const match = item.jiraTicketUrl.match(/AXON-(\d+)/);
        ticketId = match ? match[1] : null;
      }

      return {
        id: `${item.providerId}-${index}`,
        shiftType,
        requestedShift,
        requestType,
        dateRequested,
        ticketId,
        jiraTicketUrl: item.jiraTicketUrl,
      };
    });
  }, []);

  useEffect(() => {
    if (submittedRequestsData && submittedRequestsData.length > 0) {
      const transformedData = transformAPIData(submittedRequestsData);
      setSubmittedRequests(transformedData);
    } else {
      setSubmittedRequests([]);
    }
  }, [submittedRequestsData, transformAPIData]);

  const handleViewTicket = (ticketId: string) => {
    const jiraUrl = `${ticketId}`;
    window.open(jiraUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 400,
          mb: { xs: 2, md: 3 },
          fontSize: { xs: "20px", sm: "24px", md: "28px" },
        }}
      >
        Submitted Requests
      </Typography>

      <Typography
        variant="body1"
        sx={{
          mb: { xs: 2, md: 3 },
          color: "#424242",
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        Below are the blocks you've requested and are pending review. Once the
        request has been reviewed, this list will be updated.
      </Typography>

      <Box
        sx={{
          padding: { xs: 1.5, sm: 2 },
          mb: { xs: 2, md: 3 },
          border: "1px solid #10389A",
          borderRadius: "8px",
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <InfoOutlinedIcon
          sx={{
            fontSize: 20,
            color: "#1212128F",
            marginTop: "2px",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1 }}>
          {isLoading ? (
            <>
              <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 500,
                  }}
                >
                  Current Set Shift:
                </Typography>
                <Skeleton variant="text" width="60%" height={20} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontSize: { xs: "13px", sm: "14px" },
                    fontWeight: 500,
                  }}
                >
                  Additional Availability:
                </Typography>
                <Skeleton variant="text" width="50%" height={20} />
              </Box>
            </>
          ) : providerCurrentShifts.length > 0 ? (
            <>
              {(() => {
                const setShifts = providerCurrentShifts.filter(
                  (item) => item.isAdditionalShift === 0
                );
                const additionalShifts = providerCurrentShifts.filter(
                  (item) => item.isAdditionalShift === 1
                );

                return (
                  <>
                    {setShifts.length > 0 ? (
                      setShifts.map((item, index) => (
                        <Typography
                          key={item.shiftId}
                          variant="body2"
                          sx={{
                            color: "#000000",
                            fontSize: { xs: "13px", sm: "14px" },
                            wordBreak: "break-word",
                            mb: index < setShifts.length - 1 || additionalShifts.length > 0 ? 1 : 0,
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 500 }}>
                             Current Set Shift:
                          </Box>{" "}
                          {item.daysOfWeek} {item.startTime} - {item.endTime}
                        </Typography>
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                          mb: additionalShifts.length > 0 ? 1 : 0,
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 500 }}>
                         Current Set Shift:
                        </Box>{" "}
                        None
                      </Typography>
                    )}

                    {additionalShifts.length > 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 500 }}>
                          Additional Availability:
                        </Box>{" "}
                        {additionalShifts
                          .map((item, index) => {
                            const scheduleText = `${item.daysOfWeek} ${item.startTime} - ${item.endTime}`;
                            if (index === 0) return scheduleText;
                            if (index === additionalShifts.length - 1)
                              return ` and ${scheduleText}`;
                            return `, ${scheduleText}`;
                          })
                          .join("")}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#000000",
                          fontSize: { xs: "13px", sm: "14px" },
                          wordBreak: "break-word",
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 500 }}>
                          Additional Availability:
                        </Box>{" "}
                        None
                      </Typography>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: "#000000",
                  fontSize: { xs: "13px", sm: "14px" },
                  wordBreak: "break-word",
                  mb: 1,
                }}
              >
                <Box component="span" sx={{ fontWeight: 500 }}>
                 Current Set Shift:
                </Box>{" "}
                None
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#000000",
                  fontSize: { xs: "13px", sm: "14px" },
                  wordBreak: "break-word",
                }}
              >
                <Box component="span" sx={{ fontWeight: 500 }}>
                  Additional Availability:
                </Box>{" "}
                None
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {isLoadingRequests ? (
        !isTablet && !isDesktop ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {[1, 2, 3].map((index) => (
              <Box key={index} sx={{ p: 2 }}>
                <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="70%" height={20} />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Box}
            sx={{
              borderTop: "1px solid #E0E0E0",
              borderLeft: "1px solid #E0E0E0",
              borderRight: "1px solid #E0E0E0",
              borderBottom: "none",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table
              sx={{
                minWidth: { xs: "100%", sm: 650 },
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #E0E0E0",
                  fontSize: { xs: "12px", sm: "14px" },
                  padding: { xs: "12px 8px", sm: "16px" },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#E3F2FD",
                      fontWeight: 600,
                      minWidth: { xs: "100px", sm: "auto" },
                    }}
                  >
                    Shift Type
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#E3F2FD",
                      fontWeight: 600,
                      minWidth: { xs: "120px", sm: "auto" },
                    }}
                  >
                    Requested Shift
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "#E3F2FD", fontWeight: 600 }}>
                    Request Type
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#E3F2FD",
                      fontWeight: 600,
                      minWidth: { xs: "120px", sm: "auto" },
                    }}
                  >
                    Date Requested
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#E3F2FD",
                      fontWeight: 600,
                      minWidth: { xs: "120px", sm: "auto" },
                    }}
                  >
                    Submitted Ticket
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3].map((row) => (
                  <TableRow key={row}>
                    <TableCell>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="90%" height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="70%" height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="85%" height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="60%" height={20} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : submittedRequests.length === 0 ? (
        <Typography
          sx={{
            color: "#666",
            p: 2,
            fontSize: { xs: "14px", sm: "16px" },
          }}
        >
          No submitted requests found.
        </Typography>
      ) : !isTablet && !isDesktop ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {submittedRequests.map((request) => (
            <Box
              key={request.id}
              sx={{
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#000000",
                  mb: 2,
                }}
              >
                {request.shiftType}
              </Typography>

              <Typography variant="body2" sx={{ fontSize: "14px", color: "#000000", mb: 1 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>Requested Shift:</Box> {request.requestedShift.join(", ")}
              </Typography>
              
              <Typography variant="body2" sx={{ fontSize: "14px", color: "#000000", mb: 1 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>Requested Type:</Box> {request.requestType}
              </Typography>
              
              <Typography variant="body2" sx={{ fontSize: "14px", color: "#000000", mb: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>Date Requested:</Box> {request.dateRequested}
              </Typography>

              <Link
                component="button"
                onClick={() => handleViewTicket(request.jiraTicketUrl || "")}
                sx={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: 0,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: "16px" }} />
                View Submitted Ticket
              </Link>
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Box}
          sx={{
            borderTop: "1px solid #E0E0E0",
            borderLeft: "1px solid #E0E0E0",
            borderRight: "1px solid #E0E0E0",
            borderBottom: "none",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Table
            sx={{
              minWidth: { xs: "100%", sm: 650 },
              "& .MuiTableCell-root": {
                borderRight: "1px solid #E0E0E0",
                fontSize: { xs: "12px", sm: "14px" },
                padding: { xs: "12px 8px", sm: "16px" },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#E3F2FD",
                    fontWeight: 600,
                    minWidth: { xs: "100px", sm: "auto" },
                  }}
                >
                  Shift Type
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E3F2FD",
                    fontWeight: 600,
                    minWidth: { xs: "120px", sm: "auto" },
                  }}
                >
                  Requested Shift
                </TableCell>
                <TableCell sx={{ backgroundColor: "#E3F2FD", fontWeight: 600 }}>
                  Request Type
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E3F2FD",
                    fontWeight: 600,
                    minWidth: { xs: "120px", sm: "auto" },
                  }}
                >
                  Date Requested
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "#E3F2FD",
                    fontWeight: 600,
                    minWidth: { xs: "120px", sm: "auto" },
                  }}
                >
                  Submitted Ticket
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submittedRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.shiftType}</TableCell>
                  <TableCell>{request.requestedShift.join(", ")}</TableCell>
                  <TableCell>{request.requestType}</TableCell>
                  <TableCell>{request.dateRequested}</TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      onClick={() => handleViewTicket(request.jiraTicketUrl || "")}
                      sx={{
                        color: "#1976d2",
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        fontSize: { xs: "12px", sm: "14px" },
                        padding: 0,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      <OpenInNewIcon
                        sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                      />
                      View Ticket
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SubmittedRequests;
